// App change monitor: detect version and price deltas across visits.
//
// On each comparison, call saveAppSnapshot() to persist the current version
// and price per storefront. On the next visit to the same app, call
// detectChanges() to surface any changes since the last snapshot.
//
// Storage key: gar_chg_<trackId>
// All functions accept an optional `store` parameter for unit testing.

const STORAGE_PREFIX = 'gar_chg_';

/**
 * @typedef {{ version: string|null, price: number|null, formattedPrice: string|null }} CountryChangeEntry
 * @typedef {Record<string, CountryChangeEntry>} ChangeSnapshot
 * @typedef {{ country: string, type: 'version'|'price', prev: string|number, current: string|number, formattedPrice?: string|null }} AppChange
 */

/**
 * Persist the current version + price per storefront for `trackId`.
 * Call this AFTER rendering so the next visit sees the prior-visit delta.
 *
 * @param {string|number} trackId
 * @param {Array<{countryCode: string, version?: string|null, price?: number|null, formattedPrice?: string|null}>} rows
 * @param {Storage} [store]
 */
export function saveAppSnapshot(trackId, rows, store) {
  if (!trackId || !Array.isArray(rows) || rows.length === 0) return;
  const s = store ?? window.localStorage;
  const key = STORAGE_PREFIX + String(trackId);
  /** @type {ChangeSnapshot} */
  const snapshot = {};
  for (const row of rows) {
    if (!row.countryCode || !row.available) continue;
    snapshot[row.countryCode] = {
      version: row.version ?? null,
      price: typeof row.price === 'number' ? row.price : null,
      formattedPrice: row.formattedPrice ?? null,
    };
  }
  try {
    s.setItem(key, JSON.stringify(snapshot));
  } catch {
    // Storage quota exceeded — skip silently.
  }
}

/**
 * Load the stored snapshot for `trackId`.
 * Returns null if no snapshot exists.
 *
 * @param {string|number} trackId
 * @param {Storage} [store]
 * @returns {ChangeSnapshot|null}
 */
export function loadAppSnapshot(trackId, store) {
  if (!trackId) return null;
  const s = store ?? window.localStorage;
  const key = STORAGE_PREFIX + String(trackId);
  try {
    const raw = s.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Compare `prevSnapshot` to `currentRows` and return a list of detected changes.
 * Returns an empty array when there is no prior snapshot or no changes.
 *
 * @param {ChangeSnapshot|null} prevSnapshot
 * @param {Array<{countryCode: string, available?: boolean, version?: string|null, price?: number|null, formattedPrice?: string|null}>} currentRows
 * @returns {AppChange[]}
 */
export function detectChanges(prevSnapshot, currentRows) {
  if (!prevSnapshot || !Array.isArray(currentRows)) return [];
  const changes = [];
  for (const row of currentRows) {
    const cc = row.countryCode;
    if (!cc || !row.available) continue;
    const prev = prevSnapshot[cc];
    if (!prev) continue;

    const currVersion = row.version ?? null;
    const currPrice = typeof row.price === 'number' ? row.price : null;

    if (prev.version !== null && currVersion !== null && prev.version !== currVersion) {
      changes.push({ country: cc, type: 'version', prev: prev.version, current: currVersion });
    }
    if (prev.price !== null && currPrice !== null && Math.abs(prev.price - currPrice) > 0.001) {
      changes.push({
        country: cc,
        type: 'price',
        prev: prev.price,
        current: currPrice,
        formattedPrice: row.formattedPrice ?? null,
      });
    }
  }
  return changes;
}
